import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PageHeader from '../../../components/PageHeader';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { budgetService } from '../services/budgetService';
import { generateMonthRange, formatMonthLabel } from '../../../utils/monthRange';

interface BudgetRow { groupId: string; classifierId: string; groupName: string; classifierName: string; month: string; amount: number; }
interface CatRow { groupId: string; classifierId: string; groupName: string; classifierName: string; }

const ALL_MONTHS = generateMonthRange();
const formatMonth = formatMonthLabel;
const formatCurrency = (v: number) => Math.abs(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const BudgetPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState<string[]>(() => {
    try { const s = localStorage.getItem('fin_budget_months'); if (s) return JSON.parse(s); } catch {}
    const now = new Date(); return [`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`];
  });
  const [budgets, setBudgets] = useState<BudgetRow[]>([]);
  const [allRows, setAllRows] = useState<CatRow[]>([]);
  const [predicted, setPredicted] = useState<Record<string, number>>({});
  const [creditTotals, setCreditTotals] = useState<{ accountId: string; accountName: string; months: Record<string, number> }[]>([]);
  const [excludedFromTotal, setExcludedFromTotal] = useState<{ groupIds: Set<string>; classifierIds: Set<string> }>({ groupIds: new Set(), classifierIds: new Set() });

  // Group rows by group name
  const groupedData = useMemo(() => {
    const budgetLookup = new Map<string, Record<string, number>>();
    for (const b of budgets) {
      const key = `${b.groupId}||${b.classifierId}`;
      if (!budgetLookup.has(key)) budgetLookup.set(key, {});
      budgetLookup.get(key)![b.month] = b.amount;
    }

    const groupMap = new Map<string, { classifierId: string; classifierName: string; groupId: string; amounts: Record<string, number> }[]>();
    for (const r of allRows) {
      if (!groupMap.has(r.groupName)) groupMap.set(r.groupName, []);
      groupMap.get(r.groupName)!.push({
        classifierId: r.classifierId,
        classifierName: r.classifierName,
        groupId: r.groupId,
        amounts: budgetLookup.get(`${r.groupId}||${r.classifierId}`) || {},
      });
    }

    return Array.from(groupMap.entries())
      .map(([groupName, items]) => ({ groupName, items }))
      .sort((a, b) => a.groupName.localeCompare(b.groupName));
  }, [budgets, allRows]);

  const visibleMonthIndices = ALL_MONTHS.map((m, i) => selectedMonths.includes(m) ? i : -1).filter(i => i >= 0);
  const visibleMonths = visibleMonthIndices.map(i => ALL_MONTHS[i]);
  const showTotals = visibleMonths.length > 1;

  const loadBudgets = useCallback(async () => {
    setLoading(true);
    const res = await budgetService.list(selectedMonths.length ? selectedMonths : ALL_MONTHS);
    const d = res.data.data;
    // Backend returns EN field names directly
    const mappedBudgets: BudgetRow[] = (d.budgets || []).map((b: any) => ({
      groupId: b.groupId,
      classifierId: b.classifierId,
      groupName: b.groupName || '',
      classifierName: b.classifierName || '',
      month: b.month,
      amount: b.amount,
    }));
    setBudgets(mappedBudgets);
    const predMap: Record<string, number> = {};
    for (const p of (d.predicted || []) as { groupId: string; classifierId: string; month: string; amount: number }[]) {
      predMap[`${p.groupId}||${p.classifierId}||${p.month}`] = p.amount;
    }
    setPredicted(predMap);
    setCreditTotals(d.creditTotals);

    // Build unique rows from classifiers (showInBudget) + existing budgets
    const groups: { _id: string; name: string; showInBudget?: boolean; showInBudgetTotal?: boolean }[] = d.groups || [];
    const classifiers: { _id: string; groupId: string; name: string; showInBudget?: boolean; showInBudgetTotal?: boolean }[] = d.classifiers || [];
    const hiddenGroupIds = new Set(groups.filter(a => a.showInBudget === false).map(a => a._id));
    const hiddenClassifierIds = new Set(classifiers.filter(c => c.showInBudget === false).map(c => c._id));

    // Grupos e classificadores excluídos do totalizador
    setExcludedFromTotal({
      groupIds: new Set(groups.filter(a => a.showInBudgetTotal === false).map(a => a._id)),
      classifierIds: new Set(classifiers.filter(c => c.showInBudgetTotal === false).map(c => c._id)),
    });
    const groupNameMap: Record<string, string> = {};
    for (const a of groups) groupNameMap[a._id] = a.name;

    const seen = new Set<string>();
    const unique: CatRow[] = [];
    for (const c of classifiers) {
      if (hiddenClassifierIds.has(c._id) || hiddenGroupIds.has(c.groupId)) continue;
      const key = `${c.groupId}||${c._id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push({ groupId: c.groupId, classifierId: c._id, groupName: groupNameMap[c.groupId] || '', classifierName: c.name });
    }
    for (const b of mappedBudgets) {
      if (!b.classifierId) continue; // linha do agrupador — não entra como filho
      if (hiddenClassifierIds.has(b.classifierId) || hiddenGroupIds.has(b.groupId)) continue;
      const key = `${b.groupId}||${b.classifierId}`;
      if (!seen.has(key)) { seen.add(key); unique.push({ groupId: b.groupId, classifierId: b.classifierId, groupName: b.groupName || groupNameMap[b.groupId] || '', classifierName: b.classifierName || '' }); }
    }
    unique.sort((a, b) => a.groupName.localeCompare(b.groupName) || a.classifierName.localeCompare(b.classifierName));
    setAllRows(unique);
    setLoading(false);
  }, [selectedMonths]);

  useEffect(() => { loadBudgets(); }, [loadBudgets]);

  const toggleMonth = (m: string) => {
    setSelectedMonths(prev => {
      const next = prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m].sort((a, b) => a.localeCompare(b));
      localStorage.setItem('fin_budget_months', JSON.stringify(next));
      return next;
    });
  };
  const selectAllMonths = () => { setSelectedMonths(ALL_MONTHS); localStorage.setItem('fin_budget_months', JSON.stringify(ALL_MONTHS)); };
  const clearMonths = () => { setSelectedMonths([]); localStorage.setItem('fin_budget_months', JSON.stringify([])); };

  const handleCellSave = async (groupId: string, classifierId: string, month: string, value: string) => {
    const amount = parseFloat(value.replace(',', '.')) || 0;
    await budgetService.upsert({ groupId, classifierId, month, amount });
    setBudgets(prev => {
      const idx = prev.findIndex(b => b.groupId === groupId && b.classifierId === classifierId && b.month === month);
      if (idx >= 0) { const next = [...prev]; next[idx] = { ...next[idx], amount }; return next; }
      return [...prev, { groupId, classifierId, groupName: '', classifierName: '', month, amount }];
    });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader title="Orçamento" />

      {/* Month selector */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-gray-600">Competências:</span>
          <button onClick={selectAllMonths} className="text-xs text-blue-600 hover:underline">Todas</button>
          <button onClick={clearMonths} className="text-xs text-blue-600 hover:underline">Nenhuma</button>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 md:flex md:flex-wrap gap-1">
          {ALL_MONTHS.map(m => (
            <button key={m} onClick={() => toggleMonth(m)}
              className={`text-xs px-2 py-1.5 sm:py-0.5 rounded border text-center ${selectedMonths.includes(m) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
            >{formatMonth(m)}</button>
          ))}
        </div>
      </div>



      {/* Table */}
      <div className="px-1 py-4 sm:px-4 overflow-x-auto">
        <table className="w-full text-sm border rounded-lg [&_td]:align-middle [&_th]:align-middle">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 z-10">Agrupador / Classificação</th>
              {visibleMonths.map((m, i) => (
                <React.Fragment key={m}>
                  {i > 0 && <th className="w-6 text-center bg-gray-50" />}
                  <th className="px-3 py-2 text-right font-medium text-gray-600 whitespace-nowrap">{formatMonth(m)}</th>
                </React.Fragment>
              ))}
              {showTotals && <th className="px-3 py-2 text-right font-medium text-gray-900 bg-gray-100">Total</th>}
            </tr>
          </thead>
          <tbody>
            {groupedData.map(grp => <BudgetGroupRows key={grp.groupName} grp={grp} visibleMonths={visibleMonths} budgets={budgets} showTotals={showTotals} onCellSave={handleCellSave} predicted={predicted} />)}
          </tbody>
          <tfoot className="bg-gray-100 border-t">
            {creditTotals.length > 0 && (
              <>
                <tr className="border-b bg-purple-50">
                  <td className="px-3 py-2 font-semibold text-purple-800 sticky left-0 bg-purple-50">Crédito (s/ parcelas)</td>
                  {visibleMonths.map((m, i) => {
                    const total = creditTotals.reduce((s, c) => s + (c.months[m] || 0), 0);
                    return <React.Fragment key={m}>{i > 0 && <td className="bg-purple-50" />}<td className="px-3 py-2 text-right font-semibold text-purple-700">{total ? formatCurrency(total) : '—'}</td></React.Fragment>;
                  })}
                  {showTotals && <td className="px-3 py-2 text-right font-bold text-purple-800">{formatCurrency(creditTotals.reduce((s, c) => s + visibleMonths.reduce((a, m) => a + (c.months[m] || 0), 0), 0))}</td>}
                </tr>
                {creditTotals.map(acc => (
                  <tr key={acc.accountId} className="border-b">
                    <td className="px-1 py-1.5 pl-3 sm:px-3 sm:pl-6 text-gray-700 sticky left-0 bg-gray-100">{acc.accountName}</td>
                    {visibleMonths.map((m, i) => (
                      <React.Fragment key={m}>{i > 0 && <td />}<td className="px-3 py-1.5 text-right text-sm text-purple-600">{acc.months[m] ? formatCurrency(acc.months[m]) : '—'}</td></React.Fragment>
                    ))}
                    {showTotals && <td className="px-3 py-1.5 text-right font-semibold text-purple-700">{formatCurrency(visibleMonths.reduce((s, m) => s + (acc.months[m] || 0), 0))}</td>}
                  </tr>
                ))}
              </>
            )}
            <tr>
              <td className="px-3 py-2 font-semibold text-gray-900 sticky left-0 bg-gray-100">Total Geral</td>
              {visibleMonths.map((m, i) => {
                const budgetTotal = groupedData
                  .filter(g => !excludedFromTotal.groupIds.has(g.items[0]?.groupId || ''))
                  .reduce((s, g) => s + g.items
                    .filter(item => item.classifierId && !excludedFromTotal.classifierIds.has(item.classifierId))
                    .reduce((a, item) => a + (item.amounts[m] || 0), 0), 0);
                const creditTotal = creditTotals.reduce((s, c) => s + (c.months[m] || 0), 0);
                return <React.Fragment key={m}>{i > 0 && <td />}<td className="px-3 py-2 text-right font-bold">{formatCurrency(budgetTotal + creditTotal)}</td></React.Fragment>;
              })}
              {showTotals && <td className="px-3 py-2 text-right font-bold">{formatCurrency(
                groupedData
                  .filter(g => !excludedFromTotal.groupIds.has(g.items[0]?.groupId || ''))
                  .reduce((s, g) => s + visibleMonths.reduce((a, m) => a + g.items
                    .filter(item => item.classifierId && !excludedFromTotal.classifierIds.has(item.classifierId))
                    .reduce((b, item) => b + (item.amounts[m] || 0), 0), 0), 0) +
                creditTotals.reduce((s, c) => s + visibleMonths.reduce((a, m) => a + (c.months[m] || 0), 0), 0)
              )}</td>}
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

const BudgetGroupRows: React.FC<{ grp: { groupName: string; items: { classifierId: string; classifierName: string; groupId: string; amounts: Record<string, number> }[] }; visibleMonths: string[]; budgets: BudgetRow[]; showTotals: boolean; onCellSave: (groupId: string, classifierId: string, month: string, value: string) => void; predicted: Record<string, number> }> = ({ grp, visibleMonths, budgets, showTotals, onCellSave, predicted }) => {
  const groupId = grp.items[0]?.groupId || '';
  const grpBudgets = visibleMonths.map(m => {
    const budgetEntry = budgets.find(b => b.groupId === groupId && !b.classifierId && b.month === m);
    return budgetEntry?.amount || 0;
  });
  const grpTotal = grpBudgets.reduce((s, v) => s + v, 0);

  const handleResetGroup = () => {
    for (const m of visibleMonths) {
      const childrenSum = grp.items.reduce((s, item) => s + (item.amounts[m] || 0), 0);
      if (childrenSum) onCellSave(groupId, '', m, String(childrenSum));
    }
  };

  return (
    <React.Fragment>
      <tr className="bg-blue-50 border-b">
        <td className="px-3 py-2 font-semibold text-blue-800 sticky left-0 bg-blue-50">
          <div className="flex items-center gap-1.5">
            <button onClick={handleResetGroup} className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full border border-blue-300 text-blue-500 hover:bg-blue-100 hover:text-blue-700" title="Recalcular com valores propostos">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            </button>
            <span>{grp.groupName || '—'}</span>
          </div>
        </td>
        {visibleMonths.map((m, i) => {
          const childrenSum = grp.items.reduce((s, item) => s + (item.amounts[m] || 0), 0);
          const groupPredicted = !grpBudgets[i] && childrenSum > 0 ? childrenSum : undefined;
          const prevMonth = i > 0 ? visibleMonths[i - 1] : undefined;
          return (
            <React.Fragment key={m}>
              {i > 0 && <td className="bg-blue-50 w-6 text-center"><button onClick={() => onCellSave(groupId, '', m, String(grpBudgets[i - 1]))} className="text-blue-400 hover:text-blue-700 text-xs leading-none">→</button></td>}
              <td className="px-1 py-0.5 text-right bg-blue-50">
                <div className="flex items-center justify-end gap-0.5">
                  {prevMonth && grpBudgets[i] > 0 && <button onClick={() => onCellSave(groupId, '', prevMonth, String(grpBudgets[i]))} className="text-blue-400 hover:text-blue-700 text-xs leading-none shrink-0">←</button>}
                  <EditableAmount
                    value={grpBudgets[i]}
                    onSave={(v) => onCellSave(groupId, '', m, v)}
                    className="font-semibold text-blue-800"
                    predicted={groupPredicted || undefined}
                  />
                </div>
              </td>
            </React.Fragment>
          );
        })}
        {showTotals && <td className="px-3 py-2 text-right font-bold bg-blue-100 text-blue-900">{formatCurrency(grpTotal)}</td>}
      </tr>
      {grp.items.filter(item => item.classifierId).map(item => {
        const rowTotal = visibleMonths.reduce((s, m) => s + (item.amounts[m] || 0), 0);
        const hasPredicted = visibleMonths.some(m => predicted[`${groupId}||${item.classifierId}||${m}`]);
        const handleResetRow = () => {
          for (const m of visibleMonths) {
            const pred = predicted[`${groupId}||${item.classifierId}||${m}`];
            if (pred) onCellSave(groupId, item.classifierId, m, String(pred));
          }
        };
        return (
          <tr key={item.classifierId} className="border-b hover:bg-gray-50">
            <td className="px-1 py-1.5 pl-3 sm:px-3 sm:pl-6 text-gray-700 sticky left-0 bg-white whitespace-nowrap">
              <div className="flex items-center gap-1.5">
                {hasPredicted && (
                  <button onClick={handleResetRow} className="shrink-0 w-4 h-4 flex items-center justify-center rounded-full border border-gray-300 text-gray-400 hover:bg-green-50 hover:text-green-600 hover:border-green-300" title="Recalcular com valores propostos">
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  </button>
                )}
                <span>{item.classifierName || '—'}</span>
              </div>
            </td>
            {visibleMonths.map((m, i) => (
              <React.Fragment key={m}>
                {i > 0 && <td className="w-6 text-center"><button onClick={() => onCellSave(groupId, item.classifierId, m, String(item.amounts[visibleMonths[i - 1]] || 0))} className="text-gray-400 hover:text-blue-600 text-xs">→</button></td>}
                <td className="px-1 py-0.5 text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    {i > 0 && (item.amounts[m] || 0) > 0 && <button onClick={() => onCellSave(groupId, item.classifierId, visibleMonths[i - 1], String(item.amounts[m]))} className="text-gray-400 hover:text-blue-600 text-xs shrink-0">←</button>}
                    <EditableAmount
                      value={item.amounts[m] || 0}
                      onSave={(v) => onCellSave(groupId, item.classifierId, m, v)}
                      predicted={predicted[`${groupId}||${item.classifierId}||${m}`]}
                    />
                  </div>
                </td>
              </React.Fragment>
            ))}
            {showTotals && <td className="px-3 py-1.5 text-right font-semibold bg-gray-50">{rowTotal ? formatCurrency(rowTotal) : '—'}</td>}
          </tr>
        );
      })}
    </React.Fragment>
  );
};

const EditableAmount: React.FC<{ value: number; onSave: (v: string) => void; className?: string; predicted?: number }> = ({ value, onSave, className = '', predicted }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');

  if (!editing) {
    if (!value && predicted) {
      return (
        <div className="flex items-center justify-end gap-0.5 min-w-[60px]">
          <button onClick={() => { onSave(String(predicted)); }}
            className="px-1.5 py-0.5 rounded hover:bg-green-50 text-green-600 font-medium text-sm" title={`Preencher com ${formatCurrency(predicted)}`}>
            +
          </button>
          <button onClick={() => { setVal(''); setEditing(true); }}
            className="px-1.5 py-0.5 rounded hover:bg-blue-50 text-blue-600 text-sm" title="Inserir manualmente">
            ✎
          </button>
        </div>
      );
    }
    return (
      <button onClick={() => { setVal(String(value || '')); setEditing(true); }}
        className={`w-full text-right px-2 py-1 rounded hover:bg-blue-50 text-sm min-w-[60px] ${className}`}>
        {value ? formatCurrency(value) : <span className="text-gray-300">—</span>}
      </button>
    );
  }

  return (
    <input
      autoFocus
      type="text"
      value={val}
      onChange={e => setVal(e.target.value)}
      onBlur={() => { onSave(val); setEditing(false); }}
      onKeyDown={e => { if (e.key === 'Enter') { onSave(val); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
      className="w-20 border border-blue-300 rounded px-1 py-0.5 text-xs text-right focus:ring-1 focus:ring-blue-400 outline-none"
    />
  );
};

export default BudgetPage;
